import { Injectable, Injector, Optional } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";

import {
  Observable,
  BehaviorSubject,
  of,
  Subject,
  ReplaySubject,
  throwError,
  EMPTY, forkJoin
} from "rxjs";
import {
  tap,
  switchMap,
  finalize,
  catchError,
  filter,
  map, take
} from "rxjs/operators";

import { environment } from "../../../../environments/environment";
import { UserProfileService } from "../user-profile/user-profile.service";
import { FormGroup } from "@angular/forms";
import { Account } from "app/core/services/account/account";
import { RosterService } from "../roster/roster.service";
import moment from "moment";
import {
  FuseConfirmationConfig,
  FuseConfirmationService
} from "../../../../@fuse/services/confirmation";
import { Router } from "@angular/router";
import { NavigationService } from "app/core/navigation/navigation.service";
import { DEFAULT_AFTER_LOGIN } from "app/core/config/app.config";
import { LaunchDarklyService } from "app/launchdarkly.service";
import { LoginService } from "app/core/services/auth/login/login.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import {
  RequiredSkillRef,
  ResourceSkill,
  SkillReviewActionType,
  normaliseResourceSkill
} from "./models/resource-skill.model";

@Injectable({
  providedIn: "root"
})
export class AccountService {
  postAddress;
  primaryAddress;
  personalInformationForm: FormGroup;
  verificationForm: FormGroup;

  currentAccountIsSubscriber: boolean = false;

  _listAccounts = new ReplaySubject<any>();
  // TEZY2-4394 — synchronously-readable snapshot of the last account list, so
  // deactivation handling can check whether the user still has another active
  // account (the ReplaySubject has no last-value accessor).
  private _latestAccounts: any[] = [];
  _currentAccount = new BehaviorSubject<any>({});

  _connectedVenues = new BehaviorSubject<any>([]);
  _connectedResources = new BehaviorSubject<any>([]);

  _invitations = new BehaviorSubject([]);
  _memberships = new BehaviorSubject([]);
  accountAdminArray = [];

  abnInfo: any;
  newAccount: any;
  userInfo: any;
  loginError = new Subject<any>();
  closedDrawer = new Subject<boolean>();
  currentTypePlane: string;
  selectedFileUrl;
  _admins = new BehaviorSubject<any>([]);
  _shiftRoles = new BehaviorSubject<any>([]);
  currentAdmins = [];
  // updatedInvites = new Subject<any[]>();

  _skills = new BehaviorSubject<any[]>([]);

  isAdmin: boolean;

  private filterDrawerSource = new Subject<void>();
  filterDrawerSource$ = this.filterDrawerSource.asObservable();

  private filterDrawerSourceIncidentClient = new Subject<any>();
  filterDrawerSourceIncidentClient$ =
    this.filterDrawerSourceIncidentClient.asObservable();

  private filterDrawerSourceRosterClient = new Subject<any>();
  filterDrawerSourceRosterClient$ =
    this.filterDrawerSourceRosterClient.asObservable();

  _errorStatus = new Subject<any>();

  _selectedAccount = new BehaviorSubject({
    detail: {},
    venues: [],
    resources: [],
    invitations: [],
    members: [],
    documents: []
  });
  _selectedVenue = new BehaviorSubject({
    uuid: "",
    type: "",
    name: "",
    stations: [],
    roles: [],
    resource_rates: [],
    managers: [],
    documents: [],
    accreditation_requirements: []
  });
  _selectedAccountStatus = new BehaviorSubject("");
  _accreditations = new BehaviorSubject([]);
  abnForm: FormGroup;
  detailsForm: FormGroup;
  phoneForm: FormGroup;
  detailsInvalidFields = new Subject<string[]>();
  formsData = [];
  phoneNumber;

  isEditingAccount = true;
  pauseRefresh = false;
  accreditations = [];

  accountsCount = new ReplaySubject<number>(1);

  private endPoint = environment.apiUrlBusinessAccount;
  private endPointRefactored = environment.apiUrlBusinessAccountRefactored;
  private inviteEndPoint = environment.apiUrlInvite;
  private local = environment.e2e;

  constructor(
    private http: HttpClient,
    private _fuseConfirmationService: FuseConfirmationService,
    private userProfileService: UserProfileService,
    private rosterService: RosterService,
    private router: Router,
    private injector: Injector,
    private ldService: LaunchDarklyService,
    @Optional() private matSnackBar?: MatSnackBar
  ) {}
  get currentAccount$(): Observable<any> {
    return this._currentAccount.asObservable();
  }

  get currentAccount() {
    return this._currentAccount;
  }
  get selectedAccount() {
    return this._selectedAccount;
  }

  get selectedAccount$(): Observable<any> {
    return this._selectedAccount.asObservable();
  }

  get selectedVenue() {
    return this._selectedVenue;
  }
  get selectedVenue$(): Observable<any> {
    return this._selectedVenue.asObservable();
  }

  get connectedVenues() {
    return this._connectedVenues;
  }
  get connectedResources() {
    return this._connectedResources;
  }
  get selectedAccountStatus() {
    return this._selectedAccountStatus;
  }
  get admins() {
    return this._admins;
  }
  get listAccounts() {
    return this._listAccounts;
  }

  get invitations() {
    return this._invitations;
  }

  get memberships() {
    return this._memberships;
  }

  get shiftRoles() {
    return this._shiftRoles;
  }

  get detailAccreditations() {
    return this._accreditations;
  }

  get skills() {
    return this._skills;
  }


  getUserMember(account: any, userId: string): any | null {
    if (!account?.members || !userId) {
      return null;
    }
    return account.members.find((m: any) => m.user_id === userId) || null;
  }

  isUserResource(account: any, userId: string): boolean {
    const member = this.getUserMember(account, userId);
    return member?.role === 0;
  }

  /**
   * TEZY2-4394 — A member deactivated via Account > Members keeps a valid
   * session, so the front-end must detect it and block access to that account.
   * For members, `status === 0` means Active; any other defined status is
   * treated as deactivated/inactive (mirrors UtilService.getStatus). Returns
   * false when the member or its status is unknown so we never lock a user out
   * on missing data — the backend remains the authoritative access gate.
   */
  isMemberDeactivated(account: any, userId: string): boolean {
    const member = this.getUserMember(account, userId);
    return !!member && member.status != null && member.status !== 0;
  }

  /**
   * TEZY2-4394 — Does the user still have another account (other than
   * `currentUuid`) where their membership is active? Used to decide whether a
   * deactivation should suspend them (no active account left) or simply move
   * them to an account they can still use. Reads the last-known account list,
   * which carries each account's members.
   */
  hasOtherActiveAccount(currentUuid: string, userId: string): boolean {
    if (!userId) {
      return false;
    }
    return (this._latestAccounts || []).some(
      (account) =>
        account?.uuid &&
        account.uuid !== currentUuid &&
        !this.isMemberDeactivated(account, userId)
    );
  }

  set currentAccount(obj: any) {
    // this.currentAccountIsSubscriber = obj?.detail?.subscription?.plan?.plan_type === 'subscriber';
    const paymentInstruments = Array.isArray(obj?.payment_instruments)
      ? obj.payment_instruments
      : [];
    this.currentAccountIsSubscriber = paymentInstruments.length > 0;
    // currently if they have a playment instrument we call them a subscriber.
    // we can update this later when we have subscriptions running properly so be able to check.
    // every time the currentAccount is changed, we double check they still have an active subscription.
    // console.log(
    //   "*(*(*(*(*(*(*(*I RAN AND IM*)*)*)*)*)*)*)*)",
    //   this.currentAccountIsSubscriber
    // );

    obj = { ...obj };

    obj.isSubscriber = this.currentAccountIsSubscriber;
    const hasVenues = Array.isArray(obj?.venues) && obj.venues.length > 0;

    obj.isSupplier = this.currentAccountIsSubscriber || !hasVenues;
    obj.isClient = this.currentAccountIsSubscriber || hasVenues;

    if (obj && Array.isArray(obj.clients)) {
      obj.clients.forEach((client) => {
        client.isExternal = !!(
          client?.invitation?.uuid || client?.connection_request?.uuid
        );
      });
    }
    if (obj && Array.isArray(obj.suppliers)) {
      obj.suppliers.forEach((supplier) => {
        supplier.isExternal = !!(
          supplier?.invitation?.uuid || supplier?.connection_request?.uuid
        );
      });
    }

    this._currentAccount.next(obj && obj.uuid ? obj : null);
  }

  set selectedAccount(obj: any) {
    this._selectedAccount.next(obj);
  }
  set selectedVenue(obj: any) {
    this._selectedVenue.next(obj);
  }
  set connectedVenues(obj: any) {
    this._connectedVenues.next(obj);
  }
  set connectedResources(obj: any) {
    this._connectedResources.next(obj);
  }
  set selectedAccountStatus(str: any) {
    this._selectedAccountStatus.next(str);
  }
  set listAccounts(obj: any) {
    this._latestAccounts = Array.isArray(obj) ? obj : [];
    this._listAccounts.next(obj);
  }
  set admins(obj: any) {
    this._admins.next(obj);
  }

  set invitations(obj: any) {
    this._invitations.next(obj);
  }

  set memberships(obj: any) {
    this._memberships.next(obj);
  }

  set shiftRoles(obj: any) {
    this._shiftRoles.next(obj);
  }

  set detailAccreditations(array: any) {
    this._accreditations.next(array);
  }

  // get errorStatus() {
  //   return this._errorStatus.asObservable();
  // }

  // set errorStatus(obj) {
  //   this._errorStatus.next(obj);
  // }

  notifyFilterDrawerToggled(): void {
    this.filterDrawerSource?.next();
  }

  notifyFilterDrawerToggledIncidentClient(incidents): void {
    this.filterDrawerSourceIncidentClient.next(incidents);
  }

  notifyFilterDrawerToggledRosterClient(roster): void {
    this.filterDrawerSourceRosterClient.next(roster);
  }

  getConnections() {
    this.http
      .get(`${this.endPointRefactored}/connections`)
      .pipe(tap((res) => {}))
      .subscribe();
  }

  addConnection(connection) {
    return this.http.put(`${this.endPointRefactored}/connections/account`, connection);
  }

  resetSelectedAccount(status) {
    this.selectedAccountStatus = status;
    this.selectedAccount = {
      detail: {},
      venues: [],
      resources: [],
      invitations: [],
      members: [],
      documents: []
    };
  }

  setPauseRefresh(pause) {
    this.pauseRefresh = pause;
  }

  refreshCurrentAccount() {
    this.setCurrentAccount(this.currentAccount.getValue().uuid).subscribe();
  }

  regularRefreshCurrentAccount() {
    if (!this.pauseRefresh || environment.e2e) {
      let account = this.currentAccount.getValue();
      if(account?.uuid) {
        this.setCurrentAccount(account.uuid).subscribe();
      }
    }
  }

  setCurrentAccount(uuid, isFallbackSwitch = false): Observable<any> {
    if (!uuid) {
      return this.handleSetCurrentAccountFailure(
        null,
        'Account information is missing. Please sign in again.'
      );
    }
    let oldAccount = this.currentAccount.getValue();
    let currAccount;
    return this.getAccountInformation(uuid).pipe(
      switchMap((res: any) => {
        if (res && res.uuid) {
          currAccount = res;
          if (
            !currAccount.payment_instruments ||
            currAccount.payment_instruments.length === 0
          ) {
            currAccount.subscription = 0;
          } else if (
            currAccount.payment_instruments.some((pi: any) => pi.valid === true)
          ) {
            currAccount.subscription = 1;
          } else {
            currAccount.subscription = 2;
          }
          const currentUser = this.userProfileService.currentUser.getValue();
          if (currentUser?.id && oldAccount?.uuid === uuid && oldAccount?.members) {
            const oldUserMember = this.getUserMember(oldAccount, currentUser.id);
            const newUserMember = this.getUserMember(res, currentUser.id);

            const wasNotResource = oldUserMember && oldUserMember.role !== 0;
            const isNowResource = newUserMember?.role === 0;

            if (wasNotResource && isNowResource) {
              const currentUrl = this.router.url;
              if (!currentUrl.includes('/welcome') && !currentUrl.includes('/auth')) {
                this.router.navigate(['/welcome']);
              }
            }
          }

          // TEZY2-4394 — react to a mid-session (or initial-load) deactivation.
          // This chokepoint covers the 20s poll (regularRefreshCurrentAccount),
          // the initial login load, and account switching. If the user's
          // membership in the account just loaded is inactive, only suspend them
          // when they have NO other account they can still use — otherwise move
          // them to an account where they are active. `isFallbackSwitch` marks the
          // one-hop switch so we never bounce between deactivated accounts.
          const activeUser = this.userProfileService.currentUser.getValue();
          if (activeUser?.id && this.isMemberDeactivated(res, activeUser.id)) {
            const currentUrl = this.router.url;
            if (!currentUrl.includes('/auth')) {
              const activeElsewhere = isFallbackSwitch
                ? null
                : (this._latestAccounts || []).find(
                    (account) =>
                      account?.uuid &&
                      account.uuid !== res.uuid &&
                      !this.isMemberDeactivated(account, activeUser.id)
                  );

              if (activeElsewhere) {
                // Deactivated here but active elsewhere: switch them over.
                this.setCurrentAccount(activeElsewhere.uuid, true).subscribe();
              } else if (!currentUrl.includes('/account-suspended')) {
                // No account left to use → suspended screen.
                this.router.navigate(['/account-suspended']);
              }
            }
          }
          this.ldService.addOrUpdateAccountContext(res.uuid, {
            name: res?.detail?.name ?? "",
            industry: res?.detail?.industry ?? "",
            member_count: Array.isArray(res?.members) ? res?.members.length : 0,
            has_clients: Array.isArray(res?.clients) && res?.clients.length > 0,
            has_suppliers:
              Array.isArray(res?.suppliers) && res?.suppliers.length > 0,
            has_resources:
              Array.isArray(res?.resources) && res?.resources.length > 0,
            has_venues: Array.isArray(res?.venues) && res?.venues.length > 0
          });

          this.getMyAccountInvites();

          let existUuid = localStorage.getItem("currentAccount");
          if (!existUuid) {
            this.rosterService.selectedFilters = {};
            this.rosterService.selectedShifts = {};
            if (currAccount?.uuid) {
              this.router.navigate([DEFAULT_AFTER_LOGIN]);
            }
          }
          const calls = [
            this.getConnectedVenues(currAccount.uuid),
            this.getAdmins(currAccount.uuid),
          ];
          if (currAccount?.verification?.status === 'accepted') {
            calls.push(this.getConnectedResources(currAccount.uuid));
          }
          return forkJoin(calls)
        } else {
          return of([]);
        }
      }),
      switchMap(() => {
        localStorage.setItem("currentAccount", uuid);
        this.currentAccount = currAccount;
        return this._currentAccount.pipe(take(1));
      }),
      catchError((error) => {
        return this.handleSetCurrentAccountFailure(error);
      })
    );
  }

  private handleSetCurrentAccountFailure(
    error?: any,
    fallbackMessage?: string
  ) {
    this._currentAccount.next(null);
    localStorage.removeItem("currentAccount");

    const message =
      fallbackMessage ||
      error?.error?.message ||
      "We couldn't load your account. Please sign in again.";

    try {
      const snackBar = this.injector.get(MatSnackBar, null);
      snackBar?.open(message, "Dismiss", {
        duration: 5000,
        horizontalPosition: "center",
        verticalPosition: "top"
      });
    } catch (_) {
      // ignore injector errors
    }

    try {
      const loginService = this.injector.get(LoginService);
      loginService.signOut();
    } catch (_) {
      // ignore injector errors
    }

    return EMPTY;
  }

  getDetail(uuid): Observable<any> {
    if (uuid) {
      if (this.local) {
        return this.http
          .get(`${this.endPoint}/${uuid}`)
          .pipe(switchMap((res: any) => of(res.detail)));
      } else {
        return this.http.get(`${this.endPoint}/${uuid}/detail`);
      }
    } else {
      of({});
    }
  }

  updateDetail(uuid, detail): Observable<any> {
    if (this.local) {
      return this.http.get(`${this.endPoint}/${uuid}`).pipe(
        switchMap((res: any) => {
          res.detail = detail;
          return this.http.put(`${this.endPoint}/${uuid}`, res);
        })
      );
    } else {
      return this.http.put(`${this.endPoint}/${uuid}/detail`, detail);
    }
  }

  amIAdmin(accountUUID) {
    return this.accountAdminArray.includes(accountUUID);
  }

  cleanUp() {
    // this.listAccounts = [];
    // this.currentAccount = {};
    this.accountAdminArray = [];
    this.currentAdmins = [];

    this._listAccounts.complete();
    this._listAccounts = new ReplaySubject<any>();

    this._currentAccount.complete();
    this._currentAccount.next(null);
    this._currentAccount = new BehaviorSubject<any>({});

    this._connectedVenues.complete();
    this._connectedVenues = new BehaviorSubject<any>([]);
    this._connectedResources.complete();
    this._connectedResources = new BehaviorSubject<any>([]);

    this._invitations.complete();
    this._invitations = new BehaviorSubject([]);

    this._selectedAccount.complete();
    this._selectedAccount = new BehaviorSubject({
      detail: {},
      venues: [],
      resources: [],
      invitations: [],
      members: [],
      documents: []
    });

    this._selectedVenue.complete();
    this._selectedVenue = new BehaviorSubject({
      uuid: "",
      type: "",
      name: "",
      stations: [],
      roles: [],
      resource_rates: [],
      managers: [],
      documents: [],
      accreditation_requirements: []
    });
    this._selectedAccountStatus.complete();
    this._selectedAccountStatus = new BehaviorSubject("");

    this._accreditations.complete();
    this._accreditations = new BehaviorSubject([]);
    this._admins.complete();
    this._admins = new BehaviorSubject([]);

    const navService = this.injector.get(NavigationService);
    navService.refreshForAccount();
  }

  getPlanesMonth() {
    return this.http.get("api/planesMonth");
  }

  getPlanesYear() {
    return this.http.get("api/planesYear");
  }

  checkout(amount) {
    this.http
      .post("/create-checkout-session", {})
      // .pipe(
      //   switchMap(session => {
      //     return this.stripeService.redirectToCheckout({ sessionId: session['id'] })
      //   })
      // )
      .subscribe((result) => {
        // If `redirectToCheckout` fails due to a browser or network
        // error, you should display the localized error message to your
        // customer using `error.message`.
        if (result && result["error"]) {
          alert(result["error"].message);
        }
      });
  }

  getAdmins(uuid): Observable<any> {
    const request$ = this.local
      ? this.http.get("/api/admins")
      : this.http.get(`${this.endPointRefactored}/list`);

    return request$
      .pipe(
        tap((res: any) => this.setAdmins(res)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.setAdmins([]);
            this.showSnackBar("No accounts found for your user.");
            return of([]);
          } else if (error.status === 500) {
            this.setAdmins([]);
            const message =
              error?.error?.detail ||
              "Unable to load admins. Please try again later.";
            this.showSnackBar(message);
            return of([]);
          }
          return throwError(() => error);
        })
      )
  }

  private setAdmins(admins: any[]): void {
    this.admins = admins;
    this.checkIsAdmin(admins);
  }

  private showSnackBar(message: string): void {
    try {
      const snackBar = this.matSnackBar ?? this.injector.get(MatSnackBar, null);
      snackBar?.open(message, "X", {
        duration: 2000,
        verticalPosition: "top",
        horizontalPosition: "center",
      });
    } catch (err) {
      console.error("Error showing snack bar: ", err);
    }
  }

  checkIsAdmin(admins) {
    const user: any = this.userProfileService._currentUser.getValue();
    if (user && user?.id) {
      this.isAdmin = admins?.some(
        (admin) => admin?.uuid?.toString() === user?.id.toString()
      );
    }
  }

  // Skills Management
  getSkills(accountUUID: string): Observable<any> {
    if(this.local) {
      return this.http.get('/api/skills').pipe(
        tap((skills: any[]) => {
          this._skills.next(skills);
        })
      )
    }
    return this.http.get(`${this.endPointRefactored}/${accountUUID}/skills`).pipe(
      tap((skills: any[]) => {
        this._skills.next(skills);
      })
    );
  }

  createSkill(accountUUID: string, skill: any): Observable<any> {
    if(this.local) {
      return this.http.post('/api/skills', skill).pipe(
        tap((newSkill) => {
          const currentSkills = this._skills.getValue();
          this._skills.next([...currentSkills, newSkill]);
        })
      )
    }
    return this.http.post(`${this.endPointRefactored}/${accountUUID}/skills`, skill).pipe(
      tap((newSkill) => {
        const currentSkills = this._skills.getValue();
        this._skills.next([...currentSkills, newSkill]);
      })
    );
  }

  updateSkill(accountUUID: string, skill: any): Observable<any> {
    return this.http.put(`${this.endPointRefactored}/${accountUUID}/skills/${skill.uuid}`, skill).pipe(
      tap(() => {
        const currentSkills = this._skills.getValue();
        const index = currentSkills.findIndex(s => s.uuid === skill.uuid);
        if (index !== -1) {
          currentSkills[index] = skill;
          this._skills.next([...currentSkills]);
        }
      })
    );
  }

  deleteSkill(accountUUID: string, skillId: string): Observable<any> {
    return this.http.delete(`${this.endPointRefactored}/${accountUUID}/skills/${skillId}`).pipe(
      tap(() => {
        const currentSkills = this._skills.getValue();
        this._skills.next(currentSkills.filter(s => s.uuid !== skillId));
      })
    );
  }

  ///////////////////////// NEW API END POINTS FOR WHEN WE SWAP /////////////////////////

  // USER RELATED ACCOUNT METHODS
  // getMyAccountInvitesOld() {
  //   if (this.local) {
  //     combineLatest([
  //       this.http.get("api/invitations"),
  //       this.http.get("api/external-invitations"),
  //     ])
  //       .pipe(
  //         tap((res: any[]) => {
  //           this.invitations = [
  //             ...res[0],
  //             ...res[1].supplier_invitations.map((sup) => {
  //               sup.sub_account_type = "Supplier";
  //               return sup;
  //             }),
  //             ...res[1].client_invitations.map((cli) => {
  //               cli.sub_account_type = "Client";
  //               return cli;
  //             }),
  //           ];
  //         })
  //       )
  //       .subscribe();
  //   } else {
  //     combineLatest([
  //       this.http.get(`${this.endPoint}/invite`),
  //       this.http.get(`${this.endPoint}/invite-external`),
  //     ])
  //       .pipe(
  //         tap((res: any[]) => {
  //           this.invitations = [
  //             ...res[0],
  //             ...res[1].supplier_invitations.map(
  //               (sup) => (sup.sub_account_type = "Supplier")
  //             ),
  //             ...res[1].client_invitations.map((cli) => {
  //               cli.sub_account_type = "Client";
  //               return cli;
  //             }),
  //           ];
  //         })
  //       )
  //       .subscribe();
  //   }
  // }

  getAlertForCreateAccount() {
    const dialogRef = this._fuseConfirmationService.open({
      title: "Create New Account",
      message:
        "<p>Thank you for your interest in creating a new business account with our company. We appreciate your decision to join our network and look forward to serving your business needs.</p><br/>" +
        "<p>To initiate the process of setting up a new business account, we kindly request that you reach out to us via support@taskezy.com. This will allow us to gather the necessary information and guide you through the account creation procedure in a streamlined manner.</p><br/>" +
        "<p>We're excited to embark on this journey with you. We look forward to receiving your email, and our team will be in touch with you shortly.</p>",
      icon: {
        show: true,
        name: "heroicons_outline:user",
        color: "accent"
      },
      actions: {
        confirm: {
          show: true,
          label: "Ok",
          color: "primary"
        },
        cancel: {
          show: false,
          label: "Cancel"
        }
      },
      dismissible: false
    });
  }

  getMyAccountInvites() {
    if (this.local) {
      this.http
        .get("api/invitations")
        .pipe(
          tap((res: any[]) => {
            this.invitations = res;

            this.memberships = res;
          })
        )
        .subscribe();
    } else {
      this.http
        .get(`${this.endPointRefactored}/connections`)
        .pipe(
          tap((res: any) => {
            console.log("connections: ", res);
            this.invitations = [
              ...res.invitations.filter((item) => item.status !== "Active"),
              ...res.external_manage_invitations.filter(
                (item) => item.status !== "Active"
              )
            ];

            this.memberships = res.memberships;
          })
        )
        .subscribe();
    }
  }

  acceptMyAccountInvite(invite) {
    if (this.local) {
      return this.http.delete(`api/invitations/${invite.id}`);
    } else {
      return this.http.put(
        `${this.endPoint}/invite/accept/${invite.account_id}`,
        {}
      );
    }
  }

  declineMyAccountInvite(invite) {
    if (this.local) {
      return this.http.delete(`api/invitations/${invite.id}`);
    } else {
      return this.http.put(
        `${this.endPoint}/invite/decline/${invite.account_id}`,
        {}
      );
    }
  }

  getMyAccountExternalManageInvites() {
    if (this.local) {
      return this.currentAccount.pipe(
        switchMap((res: any) => of(res?.externalInvites))
      );
    } else {
      return this.http.get(`${this.endPoint}/invite-external`);
    }
  }

  acceptMyAccountExternalManageInvite(invite) {
    if (this.local) {
      return this.http.delete(`api/invitations/${invite.id}`);
    } else {
      return this.http.put(
        `${this.endPoint}/invite-external/accept/start/${invite.invite_id}`,
        {}
      );
    }
  }

  confirmMyAccountExternalManageInvite(invite) {
    if (this.local) {
      return this.http.delete(`api/invitations/${invite.id}`);
    } else {
      return this.http.put(
        `${this.endPoint}/invite-external/accept/end/${invite.invite_id}`,
        {}
      );
    }
  }

  declineMyAccountExternalManageInvite(invite) {
    if (this.local) {
      return this.http.delete(`api/invitations/${invite.id}`);
    } else {
      return this.http.put(
        `${this.endPoint}/invite-external/decline/${invite.invite_id}`,
        {}
      );
    }
  }

  isAccountExistsInTheSystem(abn) {
    if (this.local) {
      return this.http
        .get(`${this.endPoint}/${abn}`, { observe: "response" })
        .pipe(
          map((res: any) => {
            res.status = 204;
            return res;
          }),
          catchError((err) => {
            err.status = 206;
            err.statusText = "not used brah";
            return of(err);
          })
        );
    } else {
      return this.http.get(`${this.endPoint}/abn/${abn}`, {
        observe: "response"
      });
    }
  }

  getAllConnections() {
    return this.http.get(`${this.endPointRefactored}/connections`);
  }

  getAllAccounts() {
    // return this.http.get(`${this.endPoint}/list`);
    const user_id = localStorage.getItem("user_id");
    if (this.local) {
      return this.http.get(`${this.endPoint}`).pipe(
        map((res: any) => {
          res.forEach((account: any) => {
            if (
              !account.payment_instruments ||
              account.payment_instruments.length === 0
            ) {
              account.subscription = 0;
            } else if (
              account.payment_instruments.some((pi: any) => pi.valid === true)
            ) {
              account.subscription = 1;
            } else {
              account.subscription = 2;
            }
          });
          return res;
        }),
        switchMap((res: any) => {
          if (user_id === "11111111") {
            const filtered = res.filter(
              (acnt: any) =>
                acnt.members.filter(
                  (mem: any) =>
                    mem.user_id.toString() === user_id && mem.role > 0
                ).length > 0
            );
            this.listAccounts = filtered;
            this.accountsCount.next(filtered.length);
            this.accountAdminArray = filtered.map((acn) => acn.uuid);
          } else {
            this.listAccounts = [];
            this.accountsCount.next(0);
            this.accountAdminArray = [];
          }
          return this.listAccounts;
        })
      );
    } else {
      return this.http.get(`${this.endPointRefactored}/list`).pipe(
        map((res: any) => {
          res.forEach((account: any) => {
            if (
              !account.payment_instruments ||
              account.payment_instruments.length === 0
            ) {
              account.subscription = 0;
            } else if (
              account.payment_instruments.some((pi: any) => pi.valid === true)
            ) {
              account.subscription = 1;
            } else {
              account.subscription = 2;
            }
          });
          return res;
        }),
        switchMap((res: any) => {
          console.log(user_id);
          console.log("list res", res);
          const filtered = res.filter(
            (acnt: any) =>
              acnt.members.filter(
                (mem: any) => mem.user_id === user_id && mem.role > 0
              ).length > 0
          );
          console.log("fil:", filtered);
          this.listAccounts = filtered;
          this.accountsCount.next(filtered.length);
          this.accountAdminArray = filtered.map((acn) => acn.uuid);
          return of(filtered);
        }),
        catchError((error: any) => {
          if (error.status === 404) {
            this.showSnackBar("No accounts found for your user.");
            this.listAccounts = [];
            this.accountsCount.next(0);
            this.accountAdminArray = [];
            return of([]);
          } else if (error.status === 500) {
            const message =
              error?.error?.detail ||
              "Unable to load accounts. Please try again later.";
            this.showSnackBar(message);
          }
          return throwError(() => error);
        })
      );
    }
  }

  createPrimaryAccount(primaryAccount) {
    if (this.local) {
      const account = new Account();
      account.detail = primaryAccount;
      account.id = account.uuid = parseInt(
        Math.random().toString().slice(2, 11),
        10
      );
      return this.http.post(`${this.endPoint}`, account);
    } else {
      return this.http.post(`${this.endPoint}/create`, primaryAccount);
    }
  }

  checkIfUserIsAdminOfAccount(accountUUID) {
    return this.http.get(
      `${this.endPoint}/service/access/${accountUUID}/role/administrator`
    );
  }

  //GENERAL ACCOUNT METHODS
  getAccountInformation(accountUUID) {
    return this.http.get(`${this.endPointRefactored}/${accountUUID}`);
  }

  getAccountDetail(accountUUID) {
    return this.http.get(`${this.endPoint}/${accountUUID}/detail`);
  }

  putAccountDetail(accountUUID, details) {
    return this.http.put(`${this.endPoint}/${accountUUID}/detail`, details);
  }

  putAccountLogo(accountUUID, logo) {
    return this.http.put(`${this.endPoint}/${accountUUID}/detail/logo`, {
      logo
    });
  }

  getAccountFormData(accountUUID) {
    if (this.local) {
      return of(this.formsData);
      // return this.http.get("api/account-formdata").pipe(
      //   tap((res) => {
      //     this.detailAccreditations = res;
      //   })
      // );
    } else {
      return this.http.get(`${this.endPoint}/${accountUUID}/formdata`).pipe(
        tap((res) => {
          this.detailAccreditations = res;
        })
      );
    }
  }

  postAccountFormData(accountUUID, formData) {
    if (this.local) {
      this.formsData = [...this.formsData, formData];
      return of(this.formsData);
    } else {
      return this.http.post(
        `${this.endPoint}/${accountUUID}/formdata`,
        formData
      );
    }
  }

  deleteAccountFormData(accountUUID, formUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/formdata/${formUUID}`,
      { responseType: "text" }
    );
  }

  updateAccountFormData(accountUUID, formData, formUUID) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/formdata/${formUUID}`,
      formData
    );
  }

  addAccountVenues(accountUUID, venue) {
    if (this.local) {
      let currAccount;
      return this.http.get(`${this.endPoint}/${accountUUID}`).pipe(
        switchMap((res: any) => {
          venue["uuid"] = parseInt(Math.random().toString().slice(2, 55), 10);
          currAccount = res;
          currAccount.venues.push(venue);
          return this.http.post(
            `${this.endPoint}/${accountUUID}`,
            currAccount.venues
          );
        })
      );
    } else {
      return this.http
        .post(`${this.endPointRefactored}/${accountUUID}/venues`, venue)
        .pipe(
          switchMap(() => {
            return this.getConnectedVenues(accountUUID);
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 400) {
              const message =
                error?.error?.detail ||
                "Invalid venue details. Please review the form and try again.";
              this.showSnackBar(message);
            } else if (error.status === 422) {
              const message =
                error?.error?.detail ||
                "Account information missing. Please refresh and try again.";
              this.showSnackBar(message);
            } else if (error.status === 500) {
              const message =
                error?.error?.detail ||
                "Unable to add the venue right now. Please try again later.";
              this.showSnackBar(message);
            }
            return throwError(() => error);
          })
        );
    }
  }

  // getAccountVenues(accountUUID) {
  //   return this.http
  //     .get(`${this.endPoint}/${accountUUID}/venues`)
  //     .pipe(
  //       tap((res: any) => {
  //         this.connectedVenues = res;
  //       })
  //     )
  //     .subscribe();
  // }

  getAccountVenueByUUID(accountUUID, venueUUID) {
    if (this.local) {
      return this.http.get(`api/venues`).pipe(
        map((res: any) => {
          return res[0];
        })
      );
    } else {
      return this.http.get(
        `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}`
      );
    }
  }

  updateAccountVenueByUUID(accountUUID, venueUUID, venue) {
    return this.http
      .put(
        `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}`,
        venue
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            const message = error?.error?.detail || "Venue not found.";
            this.showSnackBar(message);
          } else if (error.status === 422) {
            const message =
              error?.error?.detail ||
              "Account or venue information is missing. Please refresh and try again.";
            this.showSnackBar(message);
          } else if (error.status === 500) {
            const message =
              error?.error?.detail ||
              "Unable to update the venue right now. Please try again later.";
            this.showSnackBar(message);
          }
          return throwError(() => error);
        })
      );
  }

  deleteAccountVenueByUUID(accountUUID, venueUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/venues/${venueUUID}`,
      { responseType: "text" }
    );
  }

  getAccountVenueRole(accountUUID, venueUUID, roleUUID) {
    return this.http.get(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/roles/${roleUUID}`
    );
  }

  addAccountVenueDocuments(accountUUID, venueUUID, document) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/venues/${venueUUID}/documents`,
      document
    );
  }

  getAccountVenueDocuments(accountUUID, venueUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/venue/${venueUUID}/documents`
    );
  }

  getAccountVenueDocumentByUUID(accountUUID, venueUUID, documentUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/venue/${venueUUID}/documents/${documentUUID}`
    );
  }

  updateAccountVenueDocumentByUUID(accountUUID, venueUUID, document) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/venues/${venueUUID}/documents/${document.uuid}`,
      document
    );
  }

  deleteAccountVenueDocumentByUUID(accountUUID, venueUUID, documentUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/venues/${venueUUID}/documents/${documentUUID}`,
      { responseType: "text" }
    );
  }

  addAccountVenueRole(accountUUID, venueUUID, role) {
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/roles`,
      role
    );
  }

  addAccountVenueRate(accountUUID, venueUUID, rate) {
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/rates`,
      rate
    );
  }

  updateAccountVenueRole(accountUUID, venue, roleUUID, role) {
    if (this.local) {
      return of(role);
    } else {
      return this.http.put(
        `${this.endPointRefactored}/${accountUUID}/venues/${venue?.uuid}/roles/${roleUUID}`,
        role
      );
    }
  }

  updateAccountVenueRate(accountUUID, venueUUID, rateUUID, rate) {
    return this.http.put(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/rates/${rateUUID}`,
      rate
    );
  }

  deleteAccountVenueRole(accountUUID, venueUUID, roleUUID) {
    return this.http.delete(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/roles/${roleUUID}`
    );
  }

  deleteAccountVenueRate(accountUUID, venueUUID, rateUUID) {
    return this.http.delete(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/rates/${rateUUID}`
    );
  }

  addVenueAccreditation(accountUUID, venueUUID, accreditation) {
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/accreditation`,
      accreditation
    );
  }

  deleteVenueAccreditation(accountUUID, venueUUID, templateUUID) {
    return this.http.delete(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/accreditation/${templateUUID}`
    );
  }

  updateVenueAccreditation(accountUUID, venueUUID, accreditation) {
    return this.http.put(
      `${this.endPointRefactored}/${accountUUID}/venues/${venueUUID}/accreditation`,
      accreditation
    );
  }


  getConnectedVenues(accountUUID) {
    if (this.local) {
      return this.http
        .get("api/connectedVenues")
        .pipe(
          tap((res) => {
            this.connectedVenues = res;
          })
        )
    } else {
      const currAccount = this.currentAccount.getValue();
      if (Array.isArray(currAccount?.clients) && currAccount.clients.length > 0) {
        return this.http
          .get(`${this.endPointRefactored}/${accountUUID}/connected-venues`)
          .pipe(
            tap((res) => (this.connectedVenues = res)),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 400) {
                const message =
                  error?.error?.detail ||
                  "Unable to load connected venues. Please review your request.";
                this.showSnackBar(message);
              } else if (error.status === 422) {
                const message =
                  error?.error?.detail ||
                  "Account or venue information is missing. Please refresh and try again.";
                this.showSnackBar(message);
              } else if (error.status === 500) {
                const message =
                  error?.error?.detail ||
                  "Unable to load connected venues right now. Please try again later.";
                this.showSnackBar(message);
              } else if(error.status !== 401) {
                this.connectedVenues = [];
              }
              return throwError(() => error);
            })
          )
      } else {
        const ownVenues = Array.isArray(currAccount?.venues)
          ? currAccount.venues
          : [];
        return of(ownVenues.map((venue) => {
          return {
            venue: venue,
            client: {
              name: currAccount.detail.name,
              uuid: currAccount.uuid
            }
          };
        }));
      }
    }
  }

  getShiftRoles() {
    if (this.local) {
      return this.http
        .get("api/shift-roles")
        .pipe(
          tap((res) => {
            this.shiftRoles = res;
          })
        )
        .subscribe();
    }
  }

  // MEMBERS METHODS

  updateAccountMember(accountUUID, currentMember, newMember) {
    if (this.local) {
      const member = currentMember;
      member.department = newMember.department;
      member.role = newMember.role;
      return of(member);
    } else {
      return this.http.put(
        `${this.endPoint}/${accountUUID}/members/${currentMember.user_id}`,
        newMember
      );
    }
  }

  deactivateAccountMember(accountUUID, memberUUID) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/members/deactivate/${memberUUID}`,
      null
    );
  }

  activateAccountMember(accountUUID, memberUUID) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/members/activate/${memberUUID}`,
      null
    );
  }

  activateAccountResources(accountUUID, member) {
    if (this.local) {
      let currMember = member;
      currMember.status = 1;
      return this.http.put(`api/resources/${member?.user_id}`, {
        status: currMember.status
      });
    } else {
      return this.http.put(
        `${this.endPoint}/${accountUUID}/resources/${member?.user_id}/activate`,
        null
      );
    }
  }

  // ACCOUNT INVITES METHODS
  getInvitesForAccount(accountUUID) {
    return this.http.get(`${this.endPoint}/invite/${accountUUID}`);
  }

  declineInviteForAccount(coonectionId) {
    if (this.local) {
      return EMPTY;
    } else {
      return this.http.put(
        `${this.endPointRefactored}/connections/account/decline/${coonectionId}`,
        null
      );
    }
  }

  acceptInviteForAccount(coonectionId) {
    if (this.local) {
      return EMPTY;
    } else {
      return this.http.put(
        `${this.endPointRefactored}/connections/account/accept/${coonectionId}`,
        null
      );
    }
  }

  resendInviteUserToAccount(accountUUID, email, data) {
    return this.deleteInviteUserToAccount(accountUUID, email).pipe(
      switchMap(() => this.inviteUserToAccount(accountUUID, email, data))
    );
  }

  getInviteUserToAccount(accountUUID, email) {
    return this.http.get(
      `${this.inviteEndPoint}/email/${accountUUID}/${email}`
    );
  }

  deleteInviteUserToAccount(accountUUID, email) {
    return this.http.delete(
      `${this.inviteEndPoint}/email/${accountUUID}/${email}`
    );
  }

  inviteUserToAccount(accountUUID, email, data) {
    if (this.local) {
      return of(true);
    } else {
      return this.http.post(
        `${this.inviteEndPoint}/email/${accountUUID}/${email}`,
        data
      );
    }
  }

  batchInviteUsersToAccount(accountUUID, dataArray) {
    return this.http.post(
      `${this.inviteEndPoint}/email/batch/${accountUUID}`,
      dataArray
    );
  }

  getAllAccountResources(accountUUID) {
    return this.http.get(`${this.endPoint}/${accountUUID}/resources`);
  }

  // INVITE EXTERNAL MANAGER TO SUPPLIER OR CLIENT
  updateInvitationClient(accountUUID, clientUUID, invite) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/invite`,
      invite
    );
  }

  updateInvitationSupplier(accountUUID, supplierUUID, formData) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/invite`,
      formData
    );
  }

  // ACCOUNT RESOURCES METHODS

  getResourcesCredentialsRole(account, roleUUID) {
    if (this.local) {
      const resourcesSuppliers = [];
      account?.resources.forEach((res, i) => {
        let resource = { ...res };
        resource["assessment"] = {
          "1Star": 0,
          "2Star": 0,
          "3Star": 0,
          "4Star": 0,
          "5Star": 0,
          _id: "string",
          avg: 0,
          total: 0
        };
        resource["formdata"] = [
          {
            type: "Accreditation",
            tags: ["Other induction"],
            creation_date_time: moment()
          }
        ];
        let relatedResource = {
          resource: resource,
          supplier: account.suppliers[i]
        };
        resourcesSuppliers.push(relatedResource);
      });
      return of(resourcesSuppliers);
    } else {
      return this.http.get(
        `${this.endPoint}/${account?.uuid}/resources/role/${roleUUID}`
      );
    }
  }

  getAccountResourceByUUID(accountUUID, resourceUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}`
    );
  }

  updateResourceDetail(accountUUID, resourceUUID, details) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/detail`,
      details
    );
  }

  addResourceDocuments(accountUUID, resourceUUID, documents) {
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/documents`,
      documents
    );
  }

  getResourceDocuments(accountUUID, resourceUUID) {
    return this.http.get(
      `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/documents`
    );
  }

  getResourceDocumentByUUID(accountUUID, resourceUUID, documentUUID) {
    return this.http.get(
      `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/documents/${documentUUID}`
    );
  }

  updateResourceDocumentByUUID(
    accountUUID,
    resourceUUID,
    documentUUID,
    document
  ) {
    return this.http.put(
      `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/documents/${documentUUID}`,
      document
    );
  }

  deleteResourceDocumentByUUID(accountUUID, resourceUUID, documentUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/documents/${documentUUID}`,
      { responseType: "text" }
    );
  }

  addResourceFormData(accountUUID, resourceUUID, formData) {
    if (this.local) {
      this.accreditations.push(formData);
      let currAccount = this.currentAccount.getValue();
      let resource = currAccount.resources.find(
        (r) => r.user_id === resourceUUID
      );
      resource["formdata"] = this.accreditations;
      let idx = currAccount.resources.findIndex((r) => r.uuid === resourceUUID);
      currAccount.resources.splice(idx, 1, resource);
      this.currentAccount = currAccount;
      return of("Ok");
    } else {
      return this.http.post(
        `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/formdata`,
        formData
      );
    }
  }

  getResourceFormData(accountUUID, resourceUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/formdata`
    );
  }

  getResourceFormDataByUUID(accountUUID, resourceUUID, formDataUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/formdata/${formDataUUID}`
    );
  }

  updateResourceFormDataByUUID(
    accountUUID,
    resourceUUID,
    formDataUUID,
    formData
  ) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/formdata/${formDataUUID}`,
      formData
    );
  }

  deleteResourceFormDataByUUID(accountUUID, resourceUUID, formDataUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/formdata/${formDataUUID}`,
      { responseType: "text" }
    );
  }

  getResourceRates(accountUUID, resourceUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/rate`
    );
  }

  updateResourceRate(accountUUID, resourceUUID, rate) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/resources/${resourceUUID}/rate`,
      rate
    );
  }

  deactivateResource(accountUUID, resource) {
    if (this.local) {
      let currResource = resource;
      currResource.status = 2;
      return this.http.put(`api/resources/${currResource?.user_id}`, {
        status: currResource.status
      });
    } else {
      return this.http.put(
        `${this.endPoint}/${accountUUID}/resources/${resource?.user_id}/deactivate`,
        null
      );
    }
  }

  getResourceSkills(accountUUID: string, resourceUUID: string): Observable<any[]> {
    if (this.local) {
      return this.http.get<any[]>(`/api/resource-skills/${resourceUUID}`);
    }
    return this.http.get<any[]>(
      `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/skills`
    );
  }

  /**
   * TEZY2-4333 [S10] — Read a resource's skills enriched with endorsement
   * signals: global endorsement/rejection counts and the viewing account's own
   * decision, sourced from the S6 backend read (TEZY2-4329).
   *
   * The extended `GET /{acct}/resources/{resourceId}/skills` response carries
   * `endorsementCount`, `rejectionCount`, `currentAccountReviewState`,
   * `originType` and `visibilityScope`; {@link normaliseResourceSkill} tolerates
   * camelCase/snake_case shapes. De-duplication and visibility filtering are
   * applied downstream in the display component. Read errors propagate to the
   * caller (the resource-details host clears the section on failure).
   */
  getResourceSkillsWithEndorsements(
    accountUUID: string,
    resourceUUID: string
  ): Observable<ResourceSkill[]> {
    const source$ = this.local
      ? this.http.get<any[]>(`/api/resource-skills/${resourceUUID}`)
      : this.http.get<any[]>(
          `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/skills`
        );

    return source$.pipe(
      map((raw) => {
        const list = Array.isArray(raw) ? raw : [];
        return list.map((item) => normaliseResourceSkill(item));
      })
    );
  }

  addResourceSkill(accountUUID: string, resourceUUID: string, skillUUID: string): Observable<any> {
    if (this.local) {
      return this.http.post(`/api/resource-skills/${resourceUUID}`, { uuid: skillUUID });
    }
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/skills`,
      { uuid: skillUUID }
    );
  }

  deleteResourceSkill(accountUUID: string, resourceUUID: string, skillUUID: string): Observable<any> {
    if (this.local) {
      return this.http.delete(`/api/resource-skills/${resourceUUID}/${skillUUID}`);
    }
    return this.http.delete(
      `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/skills/${skillUUID}`,
      { responseType: 'text' }
    );
  }

  /**
   * TEZY2-4334 [S11] — Set or clear the current account's own decision on a
   * resource skill (endorse / reject / remove).
   *
   * Mirrors the S3 endorse/reject/clear API (TEZY2-4326):
   *   endorse → `POST   .../skills/{resourceSkillId}/endorse`
   *   reject  → `POST   .../skills/{resourceSkillId}/reject` (optional `{ reason }`)
   *   clear   → `DELETE .../skills/{resourceSkillId}/review`
   * The endorsement counts are recomputed server-side; the fresh values are read
   * back via {@link getResourceSkillsWithEndorsements}.
   *
   * Errors propagate to the caller so the optimistic UI can roll back the change
   * (see `ResourceDetailsComponent.onSkillReviewAction`).
   */
  reviewResourceSkill(
    accountUUID: string,
    resourceUUID: string,
    skillUUID: string,
    action: SkillReviewActionType
  ): Observable<any> {
    const base = this.local
      ? `/api/resource-skills/${resourceUUID}/${skillUUID}`
      : `${this.endPointRefactored}/${accountUUID}/resources/${resourceUUID}/skills/${skillUUID}`;

    switch (action) {
      case "endorse":
        return this.http.post(`${base}/endorse`, {});
      case "reject":
        return this.http.post(`${base}/reject`, {});
      case "clear":
      default:
        return this.http.delete(`${base}/review`);
    }
  }


  // private connectedResourcesSubscription: Subscription;

  getConnectedResources(accountUUID, roleId?: string) {
    if (this.local) {
      return this.http
        .get("api/suppliers-resources")
        .pipe(
          tap((res) => {
            this.connectedResources = res;
          })
        )
    } else {
      // Add timestamp to prevent caching issues when making multiple requests
      const timestamp = new Date().getTime();
      const url = roleId 
        ? `${this.endPointRefactored}/${accountUUID}/connected-resources?role_id=${roleId}&_t=${timestamp}`
        : `${this.endPointRefactored}/${accountUUID}/connected-resources?_t=${timestamp}`;
      return this.http
        .get(url)
        .pipe(tap((res: any) => {
          this.connectedResources = res;
        }))
    }
  }

  /**
   * TEZY2-4336 [S13] — Connected resources for shift creation, filtered to
   * strict endorsed-only eligibility.
   *
   * Single entry point for BOTH shift-creation surfaces (roster
   * `ShiftFormComponent` and attendance-register `AtShiftFormComponent`) so their
   * behaviour is identical (AC4). A resource qualifies only when the account has
   * endorsed every required skill of the selected role; a role with no required
   * skills returns all connected resources.
   *
   * The strict endorsed-AND filter is applied SERVER-SIDE by the S7 backend
   * (TEZY2-4330): `GET /{acct}/connected-resources?role_id=…` returns only the
   * resources the viewing account has endorsed for all of the role's required
   * skills. Rollout is controlled entirely by the backend's feature flag, so the
   * frontend just passes the role and trusts the response — no client-side
   * endorsement fan-out.
   *
   * `requiredSkills` is used only to decide whether the role carries a skill
   * filter at all: when a role has no required skills we omit `role_id` and get
   * the full connected-resource list back.
   */
  getEligibleConnectedResources(
    accountUUID: string,
    roleId: string | null | undefined,
    requiredSkills: RequiredSkillRef[]
  ): Observable<any[]> {
    const hasRequiredSkills =
      Array.isArray(requiredSkills) && requiredSkills.length > 0;

    // Only pass role_id when the role has required skills; the backend then
    // applies the endorsed-only strict-AND filter for that role.
    const apiRoleId = hasRequiredSkills && roleId ? roleId : null;

    return this.getConnectedResources(accountUUID, apiRoleId).pipe(
      map((resources: any) => (Array.isArray(resources) ? resources : []))
    );
  }

  //ACCOUNT CLIENTS METHODS

  getClientsToAccount(accountUUID) {
    return this.http.get(`${this.endPoint}/${accountUUID}/clients`);
  }

  addClientToAccount(accountUUID, clientData) {
    if (this.local) {
      clientData.detail = clientData.account_detail;
      clientData.connection_status = 0;
      return this.http.get(`${this.endPoint}/${accountUUID}`).pipe(
        switchMap((res: any) => {
          const account = res;
          clientData.id = clientData.uuid = parseInt(
            Math.random().toString().slice(2, 11),
            10
          );
          account?.clients.push(clientData);
          return this.http.put(`${this.endPoint}/${accountUUID}`, account);
        }),
        switchMap(() => {
          return of(clientData);
        })
      );
    } else {
      return this.http
        .post(`${this.endPoint}/${accountUUID}/clients`, clientData)
        .pipe(tap((res) => (this.isEditingAccount = false)));
    }
  }

  getAccountClientByUUID(accountUUID, clientUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}`
    );
  }

  updateClientDetail(accountUUID, clientUUID, details, form) {
    if (this.local) {
      let res = {
        detail: details
      };
      res["invitation"] = {
        email: form.get("emailAdministrator").value,
        uuid: "987654321"
      };
      return of(res);
    } else {
      return this.http.put(
        `${this.endPoint}/${accountUUID}/clients/${clientUUID}`,
        details
      );
    }
  }

  addClientDocuments(accountUUID, clientUUID, documents) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/documents`,
      documents
    );
  }

  getClientDocuments(accountUUID, clientUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/documents`
    );
  }

  getClientDocumentByUUID(accountUUID, clientUUID, documentUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/documents/${documentUUID}`
    );
  }

  updateClientDocumentByUUID(accountUUID, clientUUID, documentUUID, document) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/documents/${documentUUID}`,
      document
    );
  }

  deleteClientDocumentByUUID(accountUUID, clientUUID, documentUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/documents/${documentUUID}`,
      { responseType: "text" }
    );
  }

  addClientFormData(accountUUID, clientUUID, formData) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/formdata`,
      formData
    );
  }

  getClientFormData(accountUUID, clientUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/formdata`
    );
  }

  getClientFormDataByUUID(accountUUID, clientUUID, formDataUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/formdata/${formDataUUID}`
    );
  }

  updateClientFormDataByUUID(accountUUID, clientUUID, formDataUUID, formData) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/formdata/${formDataUUID}`,
      formData
    );
  }

  deleteClientFormDataByUUID(accountUUID, clientUUID, formDataUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/formdata/${formDataUUID}`,
      { responseType: "text" }
    );
  }

  addClientVenues(accountUUID, clientUUID, venues) {
    return this.http
      .post(
        `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues`,
        venues
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400) {
            const message =
              error?.error?.detail ||
              "Invalid request. Please review the form and try again.";
            this.showSnackBar(message);
          } else if (error.status === 403) {
            const message =
              error?.error?.detail ||
              "Service not available. Account is not a subscriber.";
            this.showSnackBar(message);
          } else if (error.status === 404) {
            const message = error?.error?.detail || "Client not found.";
            this.showSnackBar(message);
          } else if (error.status === 422) {
            const message =
              error?.error?.detail ||
              "Account or Client information is missing. Please refresh and try again.";
            this.showSnackBar(message);
          } else if (error.status === 500) {
            const message =
              error?.error?.detail ||
              "Unable to add the venue right now. Please try again later.";
            this.showSnackBar(message);
          }
          return throwError(() => error);
        })
      );
  }

  getClientVenues(accountUUID, clientUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues`
    );
  }

  getVenueManagers(accountUUID, venueUUID) {
    if (this.local) {
      return this.http.get(`api/venues`).pipe(
        map((res: any) => {
          if (!res || res.length === 0) {
            throw new Error("No venues found");
          }
          let currVenue = res[0];

          return currVenue.managers;
        })
      );
    } else {
      return this.http.get(
        `${this.endPoint}/${accountUUID}/venue-managers/${venueUUID}`
      );
    }
  }

  addVenueManager(accountUUID, venueUUID, member) {
    if (this.local) {
      return this.http.get(`api/venues`).pipe(
        map((res: any) => {
          if (!res || res.length === 0) {
            throw new Error("No venues found");
          }
          let currVenue = { ...res[0] };
          currVenue.managers = currVenue.managers
            ? [...currVenue.managers, member]
            : [member];

          return currVenue.managers;
        })
      );
    } else {
      const formData = new HttpParams().set(
        "member_uuid",
        member.user_id || member.uuid
      );
      const headers = {
        headers: new HttpHeaders().set(
          "Content-Type",
          "application/x-www-form-urlencoded"
        )
      };
      return this.http.post(
        `${this.endPoint}/${accountUUID}/venue-managers/${venueUUID}`,
        formData.toString(),
        headers
      );
    }
  }

  removeVenueManager(accountUUID, venueUUID, managerUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/venue-managers/${venueUUID}/${managerUUID}`
    );
  }

  getClientVenueByUUID(accountUUID, clientUUID, venueUUID) {
    if (this.local) {
      return this.http.get(`api/venues`).pipe(
        map((res: any) => {
          return res[0];
        })
      );
    } else {
      return this.http.get(
        `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}`
      );
    }
  }

  updateClientVenueByUUID(accountUUID, clientUUID, venueUUID, venue) {
    return this.http.put(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}`,
      venue
    );
  }

  deleteClientVenueByUUID(accountUUID, clientUUID, venueUUID) {
    return this.http.delete(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}`,
      { responseType: "text" }
    );
  }

  getClientVenueRole(accountUUID, clientUUID, venueUUID, roleUUID) {
    return this.http.get(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/roles/${roleUUID}`
    );
  }

  addClientVenueRole(accountUUID, clientUUID, venueUUID, role) {
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/roles`,
      role
    );
  }

  addClientVenueRate(accountUUID, clientUUID, venueUUID, rate) {
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/rates`,
      rate
    );
  }

  addClientVenueDocument(accountUUID, clientUUID, venueUUID, document) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/documents`,
      document
    );
  }

  addVenueDocument(accountUUID, venueUUID, document) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/venues/${venueUUID}/documents`,
      document
    );
  }

  updateVenueDocument(accountUUID, venueUUID, documentUUID, document) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/venues/${venueUUID}/documents/${documentUUID}`,
      document
    );
  }

  getClientVenueDocuments(accountUUID, clientUUID, venueUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/documents`
    );
  }

  getClientVenueDocumentByUUID(
    accountUUID,
    clientUUID,
    venueUUID,
    documentUUID
  ) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/documents/${documentUUID}`
    );
  }

  updateClientVenueDocumentByUUID(
    accountUUID,
    clientUUID,
    venueUUID,
    documentUUID,
    document
  ) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/documents/${documentUUID}`,
      document
    );
  }

  deleteClientVenueDocumentByUUID(
    accountUUID,
    clientUUID,
    venueUUID,
    documentUUID
  ) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/documents/${documentUUID}`,
      { responseType: "text" }
    );
  }

  updateClientVenueRole(accountUUID, clientUUID, venueUUID, roleUUID, role) {
    if (this.local) {
      return this.http.get("api/connectedVenues").pipe(
        switchMap((res: any) => {
          let venues = res;
          let venue = venues.find((v) => v.venue.uuid === venueUUID);
          let roleIndex = venue?.venue.roles.findIndex(
            (r) => r.uuid === roleUUID
          );
          venue.venue.roles.splice(roleIndex, 1, role);
          venues[0] = venue;
          this.connectedVenues = venues;
          return this.http.put(`api/connectedVenues`, venues);
        }),
        switchMap((e) => {
          return this.getConnectedVenues(accountUUID);
        })
      );
    } else {
      return this.http.put(
        `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/roles/${roleUUID}`,
        role
      );
    }
  }

  updateClientVenueRate(accountUUID, clientUUID, venueUUID, rateUUID, rate) {
    return this.http.put(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/rates/${rateUUID}`,
      rate
    );
  }

  deleteClientVenueRole(accountUUID, clientUUID, venueUUID, roleUUID) {
    return this.http.delete(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/roles/${roleUUID}`
    );
  }

  deleteClientVenueRate(accountUUID, clientUUID, venueUUID, rateUUID) {
    return this.http.delete(
      `${this.endPointRefactored}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/rates/${rateUUID}`
    );
  }

  addClientVenueAccreditation(
    accountUUID,
    clientUUID,
    venueUUID,
    accreditation
  ) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/accreditation`,
      accreditation
    );
  }

  deleteVenueClientAccreditation(
    accountUUID,
    clientUUID,
    venueUUID,
    accreditationID
  ) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/accreditation/${accreditationID}`
    );
  }

  updateVenueClientAccreditation(
    accountUUID,
    clientUUID,
    venueUUID,
    accreditation
  ) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/clients/${clientUUID}/venues/${venueUUID}/accreditation`,
      accreditation
    );
  }

  //ACCOUNT SUPPLIERS METHODS
  addSupplierToAccount(accountUUID, supplierData): Observable<any> {
    if (this.local) {
      supplierData.detail = supplierData.account_detail;
      supplierData.connection_status = 0;
      return this.http.get(`${this.endPoint}/${accountUUID}`).pipe(
        switchMap((res: any) => {
          supplierData.id = supplierData.uuid = parseInt(
            Math.random().toString().slice(2, 11),
            10
          );
          res.suppliers.push(supplierData);
          return this.http.put(`${this.endPoint}/${accountUUID}`, res);
        }),
        switchMap(() => of(supplierData))
      );
    } else {
      return this.http
        .post(`${this.endPoint}/${accountUUID}/suppliers`, supplierData)
        .pipe(tap((res) => (this.isEditingAccount = false)));
    }
  }

  getAccountSupplierByUUID(accountUUID, supplierUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}`
    );
  }

  updateSupplierDetail(accountUUID, supplierUUID, details, form) {
    if (this.local) {
      let res = {
        detail: details
      };
      res["invitation"] = {
        email: form.get("emailAdministrator").value,
        uuid: "987654321"
      };
      return of(res);
    } else {
      return this.http.put(
        `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}`,
        details
      );
    }
  }

  addSupplierDocuments(accountUUID, supplierUUID, documents) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/documents`,
      documents
    );
  }

  getSupplierDocuments(accountUUID, supplierUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/documents`
    );
  }

  getSupplierDocumentByUUID(accountUUID, supplierUUID, documentUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/documents/${documentUUID}`
    );
  }

  updateSupplierDocumentByUUID(
    accountUUID,
    supplierUUID,
    documentUUID,
    document
  ) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/documents/${documentUUID}`,
      document
    );
  }

  deleteSupplierDocumentByUUID(accountUUID, supplierUUID, documentUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/documents/${documentUUID}`,
      { responseType: "text" }
    );
  }

  addSupplierFormData(accountUUID, supplierUUID, formData) {
    return this.http.post(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/formdata`,
      formData
    );
  }

  getSupplierFormData(accountUUID, supplierUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/formdata`
    );
  }

  getSupplierFormDataByUUID(accountUUID, supplierUUID, formDataUUID) {
    return this.http.get(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/formdata/${formDataUUID}`
    );
  }

  updateSupplierFormDataByUUID(
    accountUUID,
    supplierUUID,
    formDataUUID,
    formData
  ) {
    return this.http.put(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/formdata/${formDataUUID}`,
      formData
    );
  }

  deleteSupplierFormDataByUUID(accountUUID, supplierUUID, formDataUUID) {
    return this.http.delete(
      `${this.endPoint}/${accountUUID}/suppliers/${supplierUUID}/formdata/${formDataUUID}`,
      { responseType: "text" }
    );
  }

  //Users Forms methods

  getUserFormData() {
    return this.http
      .get(`${this.endPoint}/user/formdata`)
      .pipe(tap((res: any[]) => {}))
      .subscribe();
  }

  postUserFormData(formData) {
    return this.http.post(`${this.endPoint}/user/formdata`, formData);
  }

  deleteUserFormData(uuid) {
    return this.http.delete(`${this.endPoint}/user/formdata/${uuid}`, {
      responseType: "text"
    });
  }

  /** Translates a key, or returns the English fallback when i18n isn't set up (e.g. in some specs). */
  private t(key: string, fallback: string, params?: object): string {
    const translate = this.injector.get(TranslateService, null);
    return translate ? translate.instant(key, params) : fallback;
  }

  getResendConfirmation() {
    const data: FuseConfirmationConfig = {
      title: this.t("shared.invitations.resend.title", "Resend Invitation"),
      message: this.t("shared.invitations.resend.message", "Do you want to resend the invitation?"),
      icon: {
        show: false
      },
      actions: {
        confirm: {
          show: true,
          label: this.t("common.yes", "Yes"),
          color: "primary"
        },
        cancel: {
          show: true,
          label: this.t("common.cancel", "Cancel")
        }
      }
    };
    return data;
  }

  /** `user` is "Member" or "Resource"; other values are shown as-is. */
  getDeleteConfirmation(user) {
    const data: FuseConfirmationConfig = {
      title: this.t("shared.invitations.delete.title", "Delete Invitation"),
      message: this.t(
        "shared.invitations.delete.message",
        `Are you sure you want to delete the invitation for this ${user}?`,
        { user }
      ),
      icon: {
        show: false
      },
      actions: {
        confirm: {
          show: true,
          label: this.t("common.yes", "Yes"),
          color: "primary"
        },
        cancel: {
          show: true,
          label: this.t("common.cancel", "Cancel")
        }
      }
    };
    return data;
  }

  getLoggedInUserMedicalInfo(): Observable<any> {
    if (this.local) {
      const id = 1;
      return this.http.get(`api/accounts-medinfo/${id}`);
    } else {
      return this.http.get(`${this.endPointRefactored}/user/medinfo`);
    }
  }

  updateLoggedInUserMedicalInfo(medicalInfo): Observable<any> {
    if (this.local) {
      const id = 1;
      const body = { id, ...medicalInfo };
      return this.http.put(`api/accounts-medinfo/${id}`, body);
    }
    return this.http.put(`${this.endPointRefactored}/user/medinfo`, medicalInfo);
  }

  getUserMedicalInfo(accountUUID, resourceUUID): Observable<any> {
    return this.http.get(
      `${this.endPointRefactored}/${accountUUID}/resource/${resourceUUID}/medinfo`
    );
  }

  requestAccountVerification(accountUUID): Observable<any> {
    return this.http.post(
      `${this.endPointRefactored}/${accountUUID}/verify/request`,
      { account: accountUUID }
    );
  }
}
